import { RaidPracticeNikkeCard } from '../../raid-practice/shared/RaidPracticeNikkeCard.jsx';
import { NikkeIcon } from '../../nikke-icons/shared/NikkeIcon.jsx';
import {
  favoriteItemLabel,
  formatFavoriteItemStage,
  formatPersonalNikkeNumber,
} from '../domain/personalNikke.js';
import { PersonalNikkeEquipmentPanel } from './PersonalNikkeEquipmentPanel.jsx';

function classIconName(value) {
  const normalized = String(value || '').toLocaleLowerCase('ko-KR');
  if (/def|방어/.test(normalized)) return 'defender';
  if (/support|지원/.test(normalized)) return 'supporter';
  return 'attacker';
}

function LabeledStat({ label, value, accent = false }) {
  return (
    <div className={accent ? 'accent' : ''} title={label}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatMobileCombat(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  if (numeric < 10_000) return numeric.toLocaleString('ko-KR');
  return `${(numeric / 10_000).toFixed(1).replace(/\.0$/, '')}만`;
}

export function PersonalNikkeProgression({ personal, className = '' }) {
  const core = Number(personal.core) || 0;
  const limitBreakGrade = Math.min(3, Math.max(0, core > 0 ? 3 : Number(personal.grade) || 0));
  return (
    <span className={`personal-nikke-progression ${className}`.trim()} aria-label={`돌파 ${limitBreakGrade}/3${core > 0 ? `, 코강 ${core}` : ''}`}>
      {Array.from({ length: 3 }, (_, index) => {
        const achieved = index < limitBreakGrade;
        return (
          <NikkeIcon
            key={index}
            type="limit-break"
            iconKey={achieved ? 'limit-break' : 'unbroken'}
            aliases={achieved
              ? ['돌파', '한계돌파', 'limit-break', 'breakthrough']
              : ['미돌파', 'unbroken', 'base', 'no-limit-break']}
            label={achieved ? `${index + 1}돌파` : `${index + 1}단계 미돌파`}
            fallbackText={achieved ? '★' : '☆'}
            className="personal-nikke-progression-star"
          />
        );
      })}
      {core > 0 ? (
        <span className={`personal-nikke-progression-state${core >= 7 ? ' is-max' : ''}`}>
          <NikkeIcon
            type="limit-break"
            iconKey="core-enhance"
            aliases={['코강', '코어강화', 'core-enhance', 'core', 'core-up']}
            label={`코강 ${core}`}
            fallbackText="◆"
            className="personal-nikke-progression-icon"
          />
          <b>{core >= 7 ? 'MAX' : core}</b>
        </span>
      ) : null}
    </span>
  );
}

export function PersonalNikkeCard({ personal, mobile = false }) {
  const item = personal.catalogItem;
  if (!item) return null;
  const itemLabel = favoriteItemLabel(personal.favoriteItemTid);
  const favoriteItemStage = formatFavoriteItemStage(personal.favoriteItemTid, personal.favoriteItemLevel);

  if (mobile) {
    return (
      <article className="personal-nikke-list-card">
        <div className="personal-nikke-list-art">
          <RaidPracticeNikkeCard item={item} personal={personal} showPersonalOverload={false} />
          <span className="personal-nikke-class-icon" title={item.className || '클래스'} aria-label={item.className || '클래스'}>
            <NikkeIcon type="class" iconKey={item.className} label={item.className || '클래스'} fallbackName={classIconName(item.className)} />
          </span>
        </div>
        <div className="personal-nikke-list-main">
          <div className="personal-nikke-list-heading">
            <div className="personal-nikke-list-title">
              <strong title={item.name}>{item.name}</strong>
              <PersonalNikkeProgression personal={personal} />
            </div>
            <div className="personal-nikke-list-combat">
              <span>전투력</span>
              <strong>{formatMobileCombat(personal.combat)}</strong>
            </div>
          </div>
          <div className="personal-nikke-list-meta">
            <span><small>레벨</small><b>{formatPersonalNikkeNumber(personal.level)}</b></span>
          </div>
          <div className="personal-nikke-list-build">
            <span><small>스킬</small><b>{personal.skills.join('·')}</b></span>
            <span data-label="소장품·애장품" data-category="favorite-item" title={`${itemLabel} ${favoriteItemStage}`}><small>{itemLabel}</small><b>{favoriteItemStage}</b></span>
          </div>
          <PersonalNikkeEquipmentPanel personal={personal} />
        </div>
      </article>
    );
  }

  return (
    <article className="personal-nikke-card">
      <div className="personal-nikke-card-art">
        <RaidPracticeNikkeCard
          item={item}
          personal={personal}
          showPersonalOverload={false}
          overlay={<PersonalNikkeProgression personal={personal} className="personal-nikke-art-progression" />}
        />
        <span className="personal-nikke-class-icon" title={item.className || '클래스'} aria-label={item.className || '클래스'}>
          <NikkeIcon type="class" iconKey={item.className} label={item.className || '클래스'} fallbackName={classIconName(item.className)} />
        </span>
      </div>
      <div className="personal-nikke-card-summary" aria-label="니케 핵심 정보">
        <div className="personal-nikke-card-stats">
          <LabeledStat label="전투력" value={formatPersonalNikkeNumber(personal.combat)} accent />
          <LabeledStat label="레벨" value={formatPersonalNikkeNumber(personal.level)} />
        </div>
        <div className="personal-nikke-card-build">
          <span title="스킬 레벨"><small>스킬</small><b>{personal.skills.join('·')}</b></span>
          <span data-label="소장품·애장품" data-category="favorite-item" title={`${itemLabel} ${favoriteItemStage}`}><small>{itemLabel}</small><b>{favoriteItemStage}</b></span>
        </div>
      </div>
      <PersonalNikkeEquipmentPanel personal={personal} />
    </article>
  );
}
